/**
 * Pinia Store: Event Manager Category
 *
 * Migrated from Vuex: src/extensions/eventManager/store/modules/category.js
 */

import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import CategoryApi from '@/extensions/eventManager/store/api/category';
import { useSetupStore } from '@/stores';

const form = {
    id: undefined,
    name: '',
    description: ''
};

const rules = {
    name: 'required'
};

const ui = {
    form: {
        initialized: false,
        validateOnEnter: false
    }
};

export const useEventManagerCategoryStore = defineStore('eventManagerCategory', {
    state: () => ({
        loading: true,
        categoriesPaginated: {
            path: null,
            variables: {
                // order: 'id',
                // direction: 'asc',
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            // category: {
            //     loading: false,
            //     items: []
            // },
        },
        form: _.cloneDeep(form),
        rules: _.cloneDeep(rules),
        ui: _.cloneDeep(ui)
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized,
        formValidateOnEnter: (state) => state.ui.form.validateOnEnter
    },

    actions: {
        // Mutations converted to actions
        addPaginatedCategoriesToState(data) {
            this.categoriesPaginated.items = data.items;
            this.categoriesPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.categoriesPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.categoriesPaginated.link = _.cloneDeep(data.config.link);
            this.categoriesPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.categoriesPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.categoriesPaginated.allColumns, column => {
                let allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.categoriesPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.categoriesPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.categoriesPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.categoriesPaginated.filters, key);
            } else {
                Vue.set(this.categoriesPaginated.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            this.categoriesPaginated.variables.order = key;
            this.categoriesPaginated.variables.direction = value;
        },

        defineFilterVisibility(value) {
            this.categoriesPaginated.showFilters = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(newRules) {
            this.rules = newRules;
        },

        resetRules() {
            this.rules = _.cloneDeep(rules);
        },

        resetUI() {
            this.ui = _.cloneDeep(ui);
        },

        insertCategory(category) {
            this.form = category;
        },

        // Form field setters
        setFormInitialized(flag) {
            this.ui.form.initialized = flag;
        },

        setFormValidateOnEnter(flag) {
            this.ui.form.validateOnEnter = flag;
        },

        setFormName(value) {
            this.form.name = value;
        },

        setFormColor(value) {
            Vue.set(this.form, 'color', value);
        },

        setFormDescription(value) {
            this.form.description = value;
        },

        // Async actions
        fetchCategoriesPaginated() {
            return CategoryApi.fetchCategoriesPaginated(this.categoriesPaginated).then(data => {
                this.addPaginatedCategoriesToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchCategory(id) {
            return CategoryApi.fetchCategory(id).then(category => {
                this.insertCategory(_.cloneDeep(category));
            });
        },

        create(category) {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            category.cms = { id: cms.id };

            return CategoryApi.create(category).then(createdCategory => {
                this.insertCategory(_.cloneDeep(createdCategory));
                this.resetRules();
                return createdCategory;
            });
        },

        update(category) {
            return CategoryApi.update(category).then(updatedCategory => {
                this.insertCategory(_.cloneDeep(updatedCategory));
                this.resetRules();
                return updatedCategory;
            });
        },

        deleteCategory(id) {
            return CategoryApi.delete(id).then(() => {
                this.reset();
                this.resetRules();
                this.resetUI();
            });
        },

        reset() {
            this.insertCategory(_.cloneDeep(form));
        }
    }
});
